import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, useTheme } from '@material-ui/core';
import jwtDecode from 'jwt-decode';
import api from 'src/api/api';
import { HEADERS } from 'src/constants/headers';
import useAuth from 'src/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import {
    IMPERSONATION_API_ROUTES,
    IMPERSONATION_APP_ROUTES,
    IMPERSONATION_LIMITS,
    IMPERSONATION_STORAGE_KEYS,
    IMPERSONATION_TOKEN_TYPE,
} from 'src/modules/impersonation/constants';

type SessionInfo = {
    user?: { name?: string; email?: string };
    expiresAt?: string;
};

type ImpersonationClaims = {
    type?: string;
    exp?: number;
};

const getSessionFromStorage = (): SessionInfo | null => {
    const token = localStorage.getItem(IMPERSONATION_STORAGE_KEYS.AUTH_TOKEN);
    if (!token) return null;

    try {
        const claims: ImpersonationClaims = jwtDecode(token);
        if (claims.type !== IMPERSONATION_TOKEN_TYPE || !claims.exp)
            return null;

        const stored = sessionStorage.getItem(
            IMPERSONATION_STORAGE_KEYS.SESSION
        );
        const session = stored ? JSON.parse(stored) : {};
        return {
            ...session,
            expiresAt: new Date(
                claims.exp * IMPERSONATION_LIMITS.MILLISECONDS_PER_SECOND
            ).toISOString(),
        };
    } catch (error) {
        return null;
    }
};

const restoreOriginalSession = () => {
    const originalToken = sessionStorage.getItem(
        IMPERSONATION_STORAGE_KEYS.ORIGINAL_TOKEN
    );
    if (originalToken)
        localStorage.setItem(
            IMPERSONATION_STORAGE_KEYS.AUTH_TOKEN,
            originalToken
        );
    else localStorage.removeItem(IMPERSONATION_STORAGE_KEYS.AUTH_TOKEN);
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEYS.ORIGINAL_TOKEN);
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEYS.SESSION);
    window.location.replace(IMPERSONATION_APP_ROUTES.DASHBOARD);
};

const ImpersonationBanner = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [session] = useState<SessionInfo | null>(getSessionFromStorage);
    const [remaining, setRemaining] = useState('');
    const theme = useTheme();

    useEffect(() => {
        if (!session?.expiresAt) return;
        const update = () => {
            const milliseconds =
                new Date(session.expiresAt as string).getTime() - Date.now();
            if (milliseconds <= 0) return restoreOriginalSession();
            const minutes = Math.floor(
                milliseconds / IMPERSONATION_LIMITS.MILLISECONDS_PER_MINUTE
            );
            const seconds = Math.floor(
                (milliseconds % IMPERSONATION_LIMITS.MILLISECONDS_PER_MINUTE) /
                    IMPERSONATION_LIMITS.MILLISECONDS_PER_SECOND
            );
            setRemaining(
                `${minutes}:${String(seconds).padStart(
                    IMPERSONATION_LIMITS.TIMER_PAD_LENGTH,
                    '0'
                )}`
            );
        };
        update();
        const interval = window.setInterval(
            update,
            IMPERSONATION_LIMITS.TIMER_INTERVAL_MS
        );
        return () => window.clearInterval(interval);
    }, [session]);

    if (!session) return null;

    const end = async () => {
        try {
            await api.delete(
                IMPERSONATION_API_ROUTES.CURRENT_SESSION,
                HEADERS()
            );
        } finally {
            restoreOriginalSession();
        }
    };

    return (
        <Box
            bgcolor={theme.palette.warning.main}
            color={theme.palette.primary.contrastText}
            px={2}
            py={1}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            position="sticky"
            top={0}
            zIndex={1200}
        >
            <Typography variant="body2">
                {t('Impersonation.BannerAccessAs')}{' '}
                <strong>
                    {session.user?.name ||
                        session.user?.email ||
                        user?.name ||
                        user?.email}
                </strong>{' '}
                · {t('Impersonation.BannerPermissions')} · {remaining}
            </Typography>
            <Button size="small" variant="text" onClick={end} color="inherit">
                {t('Impersonation.EndAccess')}
            </Button>
        </Box>
    );
};

export default ImpersonationBanner;
